// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCarousel, { SwiperStyle } from "@conklin/App/Components/XngCarousel/XngCarousel";
import { xTabEnableZone } from "@conklin/App/Handlers/XNG/Common";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import Button from "@conklin/MobiusOverrides/Button/Button";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@conklin/Overrides/Components/ProductCarouselProduct";
import ProductCarouselProductCondensed from "@conklin/Overrides/Components/ProductCarouselProductCondensed";
import StyledBox, { StyledBoxStyles } from "@conklin/XDL/Components/Common/Box";
import { productsScreenRegex, SLIDES_PER_VIEW_POINTS } from "@conklin/XDL/constants";
import { generateSlidesPerViewMap } from "@conklin/XDL/Utilities";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductInfoFromList,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendImpressionTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendImpressionTracking";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import React, { createRef, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const enum productFields {
    productNumber = "productNumber",
}

const enum fields {
    title = "title",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    maxNumberOfColumns = "maxNumberOfColumns",
    widgetPosition = "widgetPosition",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    visibleElementsTitle = "visibleElementsTitle",
    titleAlignment = "titleAlignment",
    customCSS = "customCSS",
    // XNG-Change
    showAvailability = "showAvailability",
    productNumbers = "productNumbers",
    responsive = "responsive",
    viewType = "viewType",
    numOfItemsXS = "numOfItemsXS",
    numOfItemsSM = "numOfItemsSM",
    numOfItemsMD = "numOfItemsMD",
    numOfItemsLG = "numOfItemsLG",
}

interface ProductFields {
    fields: {
        [productFields.productNumber]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselType]: string;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.relatedProductType]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.maxNumberOfColumns]: number;
        [fields.widgetPosition]: number;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.visibleElementsTitle]: string;
        [fields.titleAlignment]: string;
        [fields.customCSS]: string;
        // XNG-Change
        [fields.showAvailability]: boolean;
        [fields.productNumbers]: ProductFields[];
        [fields.responsive]: boolean;
        [fields.viewType]: string;
        [fields.numOfItemsXS]: string;
        [fields.numOfItemsSM]: string;
        [fields.numOfItemsMD]: string;
        [fields.numOfItemsLG]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const products = getProductsForProductInfoList(state, ownProps.id);
    const dataViewKey = getDataViewKey({ productIds: products?.map(product => product.id) });
    const settingsCollection = getSettingsCollection(state);
    // Check if price is loaded
    const isPriceLoaded =
        products && products.length ? !!getProductInfoFromList(state, ownProps.id, products?.[0]?.id)?.pricing : false;
    return {
        products,
        errorMessage: getErrorMessage(state, ownProps.id),
        pageType: getCurrentPage(state).type,
        productRecommendationWidgets: state.components.productRecommendations.widgets,
        session: state.context.session,
        // XNG-Change
        isAuthenticated: xIsAuthenticated(state),
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        checkedProducts: (state as CustomAppState).xFrequentlyBoughtTogetherProducts[dataViewKey]?.checkedProducts,
        totalCost: (state as CustomAppState).xFrequentlyBoughtTogetherProducts[dataViewKey]?.totalCost,
        state,
        dataViewKey,
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        currency: getSession(state).currency?.currencySymbol,
        isPriceLoaded,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
    sendImpressionTracking,
    // XNG-Changes
    xTabEnableZone,
    addCartLines: makeHandlerChainAwaitable(addCartLines),
    loadCurrentCart,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ProductCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    // XNG-Changes
    swiper?: SwiperStyle;
    smallHeadingAndTextItems?: SmallHeadingAndTextStyles;
    smallHeadingAndTextPrice?: SmallHeadingAndTextStyles;
    checkBox?: CheckboxPresentationProps;
    dataWrapper?: InjectableCss;
    box?: StyledBoxStyles;
    boxWrapper?: StyledBoxStyles;
    addToCartButton?: ButtonPresentationProps;
}

export const productCarouselStyles: ProductCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        forwardAs: "p",
        css: css`
            text-align: center;
            margin-bottom: 20px;
        `,
    },
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
        `,
    },
};

const defaultCustomCss = `.wrapper {
}

.titleText {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const styles = productCarouselStyles;

const ProductCarousel = ({
    id,
    theme,
    fields,
    products,
    errorMessage,
    loadCarouselProducts,
    pageType,
    shellContext,
    productRecommendationWidgets,
    sendImpressionTracking,
    session,
    // XNG-Change
    zone,
    isAuthenticated,
    isTabletScreen,
    xTabEnableZone,
    state,
    dataViewKey,
    isMobileScreen,
    addCartLines,
    showAddToCartConfirmationDialog,
    loadCurrentCart,
    currency,
    checkedProducts,
    totalCost,
    isPriceLoaded,
}: Props) => {
    // XNG-Change
    const dispatch = useAppDispatch();
    const toasterContext = useContext(ToasterContext);

    const afterCarousel = createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = useContext(BrandStateContext).value;
    const category = useContext(CategoryContext);
    const productContext = useContext(ProductContext);
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [priceOfAllItems, setPriceOfAllItems] = useState(0);
    useEffect(() => {
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: id,
            carouselType: fields.carouselType,
            relatedProductType: fields.relatedProductType,
            seedWithManuallyAssigned: fields.seedWithManuallyAssigned,
            displayProductsFrom: fields.displayProductsFrom,
            selectedCategoryIds: fields.selectedCategoryIds,
            numberOfProductsToDisplay: fields.numberOfProductsToDisplay,
            widgetPosition: fields.widgetPosition,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
            // XNG-Change
            productNumbers: fields.productNumbers?.map((line: ProductFields) => line.fields.productNumber),
            onComplete: (props: any) => {
                if (props.products?.length > -1 && zone.includes("tab")) {
                    xTabEnableZone({ zone, isEnabled: props.products.length > 0 });
                }
            },
        } as any);
    }, [
        fields.carouselType,
        fields.relatedProductType,
        fields.seedWithManuallyAssigned,
        fields.displayProductsFrom,
        fields.selectedCategoryIds,
        fields.numberOfProductsToDisplay,
        fields.widgetPosition,
        productContext?.product?.id,
        category?.id,
        brand?.id,
        productRecommendationWidgets,
        session?.pickUpWarehouse?.id,
    ]);

    // XNG-Change
    useEffect(() => {
        if (isPriceLoaded && products && fields.viewType === "Checkboxes") {
            dispatch({
                type: "xFrequentlyBoughtTogetherProducts/selectAll",
                products,
                key: dataViewKey,
                carouselId: id,
                state,
            });
        }
    }, [isPriceLoaded]);

    // const [emblaRef, embla] = useEmblaCarousel();
    // const [windowResizeTime, setWindowResizeTime] = useState(0);
    // const [canScrollPrev, setCanScrollPrev] = useState(false);
    // const [canScrollNext, setCanScrollNext] = useState(false);
    // const setCanScroll = () => {
    //     if (shellContext.isInShell) {
    //         return;
    //     }
    //     setCanScrollPrev(!!embla && embla.canScrollPrev());
    //     setCanScrollNext(!!embla && embla.canScrollNext());
    // };

    // const getSlidesToScroll = useCallback(() => {
    //     const maxNumberOfColumns = fields.maxNumberOfColumns || 4;
    //     if (typeof window === "undefined") {
    //         return maxNumberOfColumns;
    //     }

    //     let localSlidesToScroll: number;
    //     const { innerWidth } = window;
    //     if (innerWidth < theme.breakpoints.values[1]) {
    //         localSlidesToScroll = 1;
    //     } else if (innerWidth < theme.breakpoints.values[2]) {
    //         localSlidesToScroll = 2;
    //     } else if (innerWidth < theme.breakpoints.values[3]) {
    //         localSlidesToScroll = 3;
    //     } else {
    //         localSlidesToScroll = 4;
    //     }
    //     return Math.min(localSlidesToScroll, maxNumberOfColumns);
    // }, [fields.maxNumberOfColumns]);

    // const [slidesToScroll, setSlidesToScroll] = useState(0);

    // useEffect(() => {
    //     const newSlidesToScroll = getSlidesToScroll();
    //     setSlidesToScroll(newSlidesToScroll);
    // }, [fields.maxNumberOfColumns, windowResizeTime]);

    // useEffect(() => {
    //     if (!embla) {
    //         return;
    //     }

    //     embla.on("init", setCanScroll);
    //     embla.on("select", setCanScroll);

    //     const onWindowResize = () => {
    //         setWindowResizeTime(Date.now());
    //     };

    //     window.addEventListener("resize", onWindowResize);
    //     onWindowResize();

    //     return () => {
    //         window.removeEventListener("resize", onWindowResize);
    //     };
    // }, [embla]);

    // useEffect(() => {
    //     if (!embla || !products || products.length === 0) {
    //         return;
    //     }

    //     embla.reInit({
    //         align: "start",
    //         slidesToScroll,
    //         draggable: isTouchDevice(),
    //         loop: products.length > slidesToScroll,
    //     });
    //     setCanScroll();
    // }, [embla, slidesToScroll, products]);

    useEffect(() => {
        if (
            fields.carouselType !== "recommendedProducts" ||
            !productRecommendationWidgets ||
            fields.widgetPosition > productRecommendationWidgets.length ||
            !products
        ) {
            return;
        }

        const productRecommendationWidget = productRecommendationWidgets[fields.widgetPosition - 1];
        const recommendationIds: number[] = [];
        products?.forEach(o => {
            const id = productRecommendationWidget.recs.find(r => r.refCode === o.detail?.name)?.id;
            if (id) {
                recommendationIds.push(id);
            }
        });

        if (recommendationIds.length) {
            sendImpressionTracking({ recommendationIds });
        }
    }, [fields.carouselType, productRecommendationWidgets, products]);

    const getProductRecommendationId = (product: ProductModel) => {
        if (!productRecommendationWidgets || fields.widgetPosition > productRecommendationWidgets.length) {
            return;
        }

        const productRecommendationWidget = productRecommendationWidgets[fields.widgetPosition - 1];
        return productRecommendationWidget.recs.find(r => r.refCode === product.detail?.name)?.id;
    };

    // <XNG-Change>
    const handleChanged = (_: React.SyntheticEvent, product: ProductModel) => {
        dispatch({
            type: "xFrequentlyBoughtTogetherProducts/updateSelection",
            productInfo: getProductInfoFromList(state, id, product.id),
            key: dataViewKey,
        });
    };

    const addToCartClickHandler = async (e: any) => {
        e.preventDefault();
        setIsAddingToCart(true);
        let selectedLines: ProductInfo[] = [];

        if (!checkedProducts || !Object.keys(checkedProducts) || !Object.keys(checkedProducts).length) {
            products?.forEach(product => {
                const info = getProductInfoFromList(state, id, product.id);
                info && selectedLines.push(info);
            });
        } else {
            selectedLines = Object.values(checkedProducts);
        }

        // XNG-Changes
        const linesToAdd = selectedLines?.map(o =>
            o.pricing?.unitNetPrice === 0 ? { ...o, properties: { lineQuoteRequested: "true" } } : o,
        );

        if (!linesToAdd || linesToAdd.length === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            setIsAddingToCart(false);
            return;
        }
        const cartLinesCollection = (await addCartLines({ productInfos: linesToAdd })) as CartLineCollectionModel;
        if (showAddToCartConfirmationDialog) {
            const isQtyAdjusted = cartLinesCollection.cartLines?.some(cartLine => cartLine.isQtyAdjusted) ?? false;

            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={isQtyAdjusted} multipleProducts={true} />,
                messageType: "success",
            });
        }
        if (cartLinesCollection) {
            loadCurrentCart({ shouldLoadFullCart: false });
        }
        setIsAddingToCart(false);
    };
    // </XNG-Change>

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!products || products.length === 0) {
        return null;
    }

    // const title =
    //     fields.title ||
    //     translate(carouselTypeOptions.find(o => o.value === fields.carouselType)?.displayName || "Product Carousel");
    const showBrandBlocks = fields.showBrandName && products.some(o => !!o.brand);

    // XNG-Change
    const breakpoints = generateSlidesPerViewMap({
        [SLIDES_PER_VIEW_POINTS.xs]: parseFloat(fields.numOfItemsXS) || (fields.viewType === "Checkboxes" ? 1.8 : 2),
        [SLIDES_PER_VIEW_POINTS.sm]:
            parseFloat(fields.numOfItemsSM) || (fields.viewType === "Condensed" || isTabletScreen ? 2.25 : 2.8),
        [SLIDES_PER_VIEW_POINTS.md]: parseFloat(fields.numOfItemsMD) || 3,
        [SLIDES_PER_VIEW_POINTS.lg]: parseFloat(fields.numOfItemsLG) || (fields.viewType === "Checkboxes" ? 4.2 : 3.8),
    });

    // XNG-Changes
    const customCssWrapper = {
        css: css`
            @media print {
                display: none;
            }
            ${fields.customCSS}
        `,
    };
    const CarouselCard = fields.viewType === "Condensed" ? ProductCarouselProductCondensed : ProductCarouselProduct;
    return (
        <StyledWrapper {...customCssWrapper}>
            <>
                <SkipNav
                    text={translate("Skip Carousel")}
                    extendedStyles={styles.skipCarouselButton}
                    destination={afterCarousel}
                />
                {/* xng-change */}
                {fields.title && (
                    <ContentWrapper alignment={fields.titleAlignment} className="wrapper">
                        <Typography {...styles.titleText}>{fields.title}</Typography>
                    </ContentWrapper>
                )}
                {/* xng-change */}
                <StyledWrapper {...styles.dataWrapper} {...{ displayAsGrid: fields.viewType === "Checkboxes" }}>
                    <StyledWrapper
                        {...styles.carouselWrapper}
                        // XNG-Changes
                        {...{ carouselType: fields.carouselType }}
                    >
                        <XngCarousel
                            spaceBetween={fields.viewType === "Condensed" || isTabletScreen ? 22 : 24}
                            navigation={!isTabletScreen}
                            pagination={false}
                            breakpoints={fields.responsive ? undefined : breakpoints}
                            draggable={false}
                            scrollbar
                            className={!isTabletScreen ? "xng-swiper-with-shadow" : ""}
                            extendedStyles={styles.swiper}
                            arrowNavigationType="circle"
                            // XNG-Changes
                            centerInsufficientSlides={false}
                        >
                            {products.map(product => (
                                <CarouselCard
                                    key={product.id}
                                    carouselId={id}
                                    product={product}
                                    recommendationId={getProductRecommendationId(product)}
                                    showImage={fields.showImage}
                                    showBrand={showBrandBlocks}
                                    showTitle={fields.showTitle}
                                    showPartNumbers={fields.showPartNumbers}
                                    showPrice={fields.showPrice}
                                    showAddToCart={fields.showAddToCart}
                                    showAddToList={fields.showAddToList}
                                    showImageBadges={fields.showImageBadges}
                                    maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                    showTextBadges={fields.showTextBadges}
                                    maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                    extendedStyles={styles.carouselProductStyles}
                                    // XNG-Changes
                                    showAvailability={fields.showAvailability}
                                    actionsTemplate={(product: ProductModel) => {
                                        return (
                                            fields.viewType === "Checkboxes" && (
                                                <Checkbox
                                                    {...styles.checkBox}
                                                    checked={checkedProducts && !!checkedProducts[product.id]}
                                                    onChange={e => handleChanged(e, product)}
                                                    data-test-selector="productCarousel_SelectCheckbox"
                                                />
                                            )
                                        );
                                    }}
                                />
                            ))}
                        </XngCarousel>
                        <span ref={afterCarousel} tabIndex={-1} />
                    </StyledWrapper>
                    {fields.viewType === "Checkboxes" && (
                        <StyledWrapper {...styles.boxWrapper}>
                            <StyledBox extendedStyles={styles.box}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.smallHeadingAndTextItems}
                                    text={
                                        checkedProducts && Object.keys(checkedProducts)
                                            ? Object.keys(checkedProducts).length.toString()
                                            : "0"
                                    }
                                    heading={translate("Selected Items")}
                                />
                                <SmallHeadingAndText
                                    extendedStyles={styles.smallHeadingAndTextPrice}
                                    text={`${currency}${(totalCost || 0)?.toFixed(2)}`}
                                    heading={translate("Bundle Price")}
                                />
                                <Button
                                    {...styles.addToCartButton}
                                    disabled={isAddingToCart || !Object.keys(checkedProducts || {})?.length}
                                    onClick={addToCartClickHandler}
                                >
                                    {translate("Add Selected to Cart")}
                                </Button>
                            </StyledBox>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </>
        </StyledWrapper>
    );
};

const carouselTypeOptions = [
    { value: "crossSells", displayName: "Cross Sells" },
    { value: "customersAlsoPurchased", displayName: "Customers Also Purchased" },
    { value: "recentlyViewed", displayName: "Recently Viewed" },
    { value: "relatedProducts", displayName: "Related Products" },
    { value: "topSellers", displayName: "Top Sellers" },
    { value: "recommendedProducts", displayName: "Recommended Products" },
    // XNG-Change
    { value: "featuredProducts", displayName: "Featured Products" },
];

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(ProductCarousel))),
    definition: {
        group: "Common",
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.titleAlignment,
                displayName: "Title Alignment",
                editorTemplate: "RadioButtonsField",
                defaultValue: "center",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                tab: basicTab,
                options: [
                    {
                        displayName: "Left",
                        value: "flex-start",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "flex-end",
                    },
                ],
            },
            // XNG-Change: for PDP we need responsive "auto type"
            {
                name: fields.responsive,
                displayName: "Responsive mode",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.carouselType,
                displayName: "Carousel Type",
                editorTemplate: "DropDownField",
                options: carouselTypeOptions,
                defaultValue: "crossSells",
                fieldType: "Contextual",
                sortOrder: 3,
                tab: basicTab,
                customFilter: (item, pageType, settings) => {
                    const {
                        settingsCollection: {
                            websiteSettings: { enableDynamicRecommendations },
                            productSettings: { enableProductRecommendations },
                        },
                    } = settings;

                    if (!enableDynamicRecommendations && item.value === "topSellers") {
                        return false;
                    }

                    if (!enableProductRecommendations && item.value === "recommendedProducts") {
                        return false;
                    }

                    if (
                        (pageType.type !== "ProductDetailsPage" && item.value === "relatedProducts") ||
                        (pageType.type !== "ProductDetailsPage" &&
                            pageType.type !== "CartPage" &&
                            item.value === "customersAlsoPurchased")
                    ) {
                        return false;
                    }

                    return true;
                },
            },
            {
                name: fields.displayProductsFrom,
                displayName: "Display Products From",
                editorTemplate: "DropDownField",
                options: [
                    { value: "allCategories", displayName: "All Categories" },
                    { value: "selectedCategories", displayName: "Selected Categories" },
                    { value: "customerSegments", displayName: "Customer Segments" },
                ],
                defaultValue: "allCategories",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                sortOrder: 4,
                tab: basicTab,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "topSellers" &&
                    item.fields[fields.displayProductsFrom] === "selectedCategories",
                sortOrder: 5,
                tab: basicTab,
            },
            {
                name: fields.relatedProductType,
                displayName: "Related Product Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "relatedProducts",
                sortOrder: 6,
                tab: basicTab,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 16,
                defaultValue: 10,
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "customersAlsoPurchased" ||
                    item.fields[fields.carouselType] === "topSellers",
                sortOrder: 7,
                tab: basicTab,
            },
            {
                name: fields.seedWithManuallyAssigned,
                displayName: "Seed With Manually Assigned",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: (item, _advancedFeaturesEnabled, _theme, pageType) =>
                    pageType === "ProductDetailsPage" && item.fields[fields.carouselType] === "customersAlsoPurchased",
                sortOrder: 8,
                tab: basicTab,
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 4,
                defaultValue: 4,
                fieldType: "Contextual",
                sortOrder: 9,
                tab: basicTab,
                // XNG-Change
                isVisible: item => false,
            },
            {
                name: fields.widgetPosition,
                displayName: "Widget Position",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 1,
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "recommendedProducts",
                sortOrder: 10,
                tab: basicTab,
            },
            {
                name: fields.visibleElementsTitle,
                displayName: "Visible Elements",
                editorTemplate: "LabelField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 11,
                tab: basicTab,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                sortOrder: 12,
                tab: basicTab,
                // XNG-Change
                isVisible: item => false,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "Contextual",
                sortOrder: 13,
                tab: basicTab,
                // XNG-Change
                isVisible: item => false,
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 14,
                tab: basicTab,
                // XNG-Change
                isVisible: item => false,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 15,
                tab: basicTab,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 16,
                tab: basicTab,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 17,
                tab: basicTab,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 18,
                tab: basicTab,
            },
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 19,
                tab: basicTab,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                // XNG-Change
                isVisible: widget => false,
                defaultValue: "3",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 19,
                tab: basicTab,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 20,
                tab: basicTab,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                // XNG-Change
                isVisible: item => false,
                defaultValue: "1",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 21,
                tab: basicTab,
            },
            // XNG-Change showAvailability
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 22,
            },
            // XNG-Change view type
            {
                name: fields.viewType,
                displayName: "View Type",
                editorTemplate: "DropDownField",
                defaultValue: "Regular",
                options: [
                    { displayName: "Regular View", value: "Regular" },
                    { displayName: "Condensed View", value: "Condensed" },
                    { displayName: "Checkboxes View", value: "Checkboxes" },
                ],
                fieldType: "Contextual",
                sortOrder: 23,
                hideEmptyOption: true,
            },
            // XNG-Change
            {
                name: fields.productNumbers,
                displayName: "Featured Product Numbers",
                editorTemplate: "ListField",
                isRequired: true,
                getDisplay: (item: HasFields) => {
                    return item.fields.productNumber;
                },
                fieldDefinitions: [
                    {
                        name: productFields.productNumber,
                        displayName: "ID",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
                fieldType: "General",
                defaultValue: [],
                isVisible: item => item.fields[fields.carouselType] === "featuredProducts",
                validate: item => (item.length > 12 ? "You can select up to 12 products!" : ""),
            },
            // <XNG-Changes>
            {
                name: fields.numOfItemsXS,
                displayName: "Products on Screens < 480px  ",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "1",
                tab: advancedTab,
                validate: (value, item) => {
                    if (value === "1") {
                        return "";
                    }
                    if (!productsScreenRegex.test(value)) {
                        return "Value should be from 1 - 6";
                    }
                    return "";
                },
            },
            {
                name: fields.numOfItemsSM,
                displayName: "Products on Screens < 1024px  ",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "2.2",
                tab: advancedTab,
                validate: (value, item) => {
                    if (value === "") {
                        return "";
                    }
                    if (!productsScreenRegex.test(value)) {
                        return "Value should be from 1 - 6";
                    }
                    return "";
                },
            },
            {
                name: fields.numOfItemsMD,
                displayName: "Products on Screens < 1366px  ",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "3.8",
                tab: advancedTab,
                validate: (value, item) => {
                    if (value === "") {
                        return "";
                    }
                    if (!productsScreenRegex.test(value)) {
                        return "Value should be from 1 - 6";
                    }
                    return "";
                },
            },
            {
                name: fields.numOfItemsLG,
                displayName: "Products on Screens > 1366px  ",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "4.5",
                tab: advancedTab,
                validate: (value, item) => {
                    if (value === "") {
                        return "";
                    }
                    if (!productsScreenRegex.test(value)) {
                        return "Value should be from 1 - 6";
                    }
                    return "";
                },
            },
            // </XNG-Changes>
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
